// src/pages/finance-portal/fixed-assets/FixedAssetAgent.js
//
// Gemini-powered Fixed Asset Agent
//
// Three skills surfaced as tabs:
//   1. Capitalize-or-Expense (recommendation for a new purchase)
//   2. Impairment Analyst (review a trigger event for an asset)
//   3. Asset Q&A chat
//
// All skills hit /api/finance/fixed-assets/agent/* which calls Gemini
// (services/GeminiService.py) and logs token usage to LLMCallLog.

import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
    Grid, IconButton, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK,
    formatCurrency, humanizeStatus,
} from './components/fixedAssetFormatters';

const SKILLS = [
    { key: 'capitalize', label: 'Capitalize vs. Expense' },
    { key: 'impairment', label: 'Impairment Analyst' },
    { key: 'chat', label: 'Asset Q&A' },
];

export default function FixedAssetAgent() {
    const { enqueueSnackbar } = useSnackbar();
    const [skill, setSkill] = useState('capitalize');
    const [assets, setAssets] = useState([]);

    // capitalize
    const [capForm, setCapForm] = useState({ description: '', cost: '', vendor: '', threshold: 2500 });
    const [capLoading, setCapLoading] = useState(false);
    const [capResult, setCapResult] = useState(null);

    // impairment
    const [impForm, setImpForm] = useState({
        asset_id: '', trigger_event_description: '',
        undiscounted_cash_flows: '', fair_value: '', persist: false,
    });
    const [impLoading, setImpLoading] = useState(false);
    const [impResult, setImpResult] = useState(null);

    // chat
    const [chatAsset, setChatAsset] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/finance/fixed-assets/assets', { params: { per_page: 200 } })
            .then(r => { if (r.data?.success) setAssets(r.data.data || []); })
            .catch(() => {});
    }, []);

    const handleCapitalize = async () => {
        if (!capForm.description || !capForm.cost) {
            enqueueSnackbar('Description and cost required', { variant: 'warning' });
            return;
        }
        setCapLoading(true);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/agent/recommend-capitalization', {
                description: capForm.description,
                cost: parseFloat(capForm.cost),
                vendor: capForm.vendor,
                threshold: parseFloat(capForm.threshold) || 2500,
            });
            if (res.data?.success) setCapResult(res.data.data);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'AI call failed', { variant: 'error' });
        } finally {
            setCapLoading(false);
        }
    };

    const handleImpairment = async () => {
        if (!impForm.asset_id || !impForm.trigger_event_description) {
            enqueueSnackbar('Pick an asset and describe the trigger', { variant: 'warning' });
            return;
        }
        setImpLoading(true);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/agent/analyze-impairment', {
                asset_id: parseInt(impForm.asset_id, 10),
                trigger_event_description: impForm.trigger_event_description,
                undiscounted_cash_flows: impForm.undiscounted_cash_flows
                    ? parseFloat(impForm.undiscounted_cash_flows) : null,
                fair_value: impForm.fair_value ? parseFloat(impForm.fair_value) : null,
                persist: impForm.persist,
            });
            if (res.data?.success) setImpResult(res.data.data);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'AI call failed', { variant: 'error' });
        } finally {
            setImpLoading(false);
        }
    };

    const handleChat = async () => {
        if (!chatInput.trim()) return;
        const userMsg = { role: 'user', text: chatInput };
        setChatHistory(prev => [...prev, userMsg]);
        setChatLoading(true);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/agent/chat', {
                question: chatInput,
                asset_id: chatAsset || null,
            });
            if (res.data?.success) {
                setChatHistory(prev => [...prev, { role: 'assistant', text: res.data.data?.answer || '' }]);
            }
            setChatInput('');
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Chat failed', { variant: 'error' });
        } finally {
            setChatLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 30, color: '#fff' }} />
                    <Box>
                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                            Fixed Asset Agent
                        </Typography>
                        <Typography sx={{ fontFamily: FA_FONT, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                            Gemini-powered: capitalization, impairment, and asset Q&A
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {SKILLS.map(s => (
                        <Chip key={s.key} label={s.label}
                            onClick={() => setSkill(s.key)}
                            sx={{ fontFamily: FA_FONT, fontWeight: 600, cursor: 'pointer',
                                bgcolor: skill === s.key ? FA_ACCENT : '#f1f5f9',
                                color: skill === s.key ? '#fff' : '#475569' }} />
                    ))}
                </Box>

                {skill === 'capitalize' && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily: FA_FONT, fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                                Should I capitalize this purchase?
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField fullWidth size="small" multiline minRows={2} label="Item Description"
                                        value={capForm.description}
                                        onChange={e => setCapForm({ ...capForm, description: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField fullWidth size="small" type="number" label="Cost ($)"
                                        value={capForm.cost}
                                        onChange={e => setCapForm({ ...capForm, cost: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField fullWidth size="small" label="Vendor"
                                        value={capForm.vendor}
                                        onChange={e => setCapForm({ ...capForm, vendor: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField fullWidth size="small" type="number" label="Threshold ($)"
                                        value={capForm.threshold}
                                        onChange={e => setCapForm({ ...capForm, threshold: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                </Grid>
                            </Grid>
                            <Box sx={{ mt: 2 }}>
                                <Button variant="contained" startIcon={<AutoAwesomeIcon />}
                                    onClick={handleCapitalize} disabled={capLoading}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                                        '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                                    {capLoading ? 'Asking Gemini...' : 'Get Recommendation'}
                                </Button>
                            </Box>

                            {capResult && (
                                <Box sx={{ mt: 3, p: 2.5, bgcolor: '#f5f3ff', borderRadius: '12px',
                                    border: `1px solid ${FA_ACCENT}33` }}>
                                    {capResult.recommendation ? (
                                        <>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                <Chip
                                                    label={capResult.recommendation.decision || 'UNKNOWN'}
                                                    sx={{ fontFamily: FA_FONT, fontWeight: 700,
                                                        bgcolor: capResult.recommendation.decision === 'CAPITALIZE' ? '#dcfce7' : '#fee2e2',
                                                        color: capResult.recommendation.decision === 'CAPITALIZE' ? '#166534' : '#991b1b' }} />
                                                {capResult.recommendation.asset_class && (
                                                    <Chip label={humanizeStatus(capResult.recommendation.asset_class)}
                                                        sx={{ fontFamily: FA_FONT, fontWeight: 600 }} />
                                                )}
                                                {capResult.recommendation.useful_life_months && (
                                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.85rem' }}>
                                                        Life: <b>{capResult.recommendation.useful_life_months} mo</b>
                                                    </Typography>
                                                )}
                                                {capResult.recommendation.confidence != null && (
                                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.85rem', color: '#64748b' }}>
                                                        Confidence {capResult.recommendation.confidence}%
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.88rem', color: '#475569' }}>
                                                {capResult.recommendation.reasoning}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap' }}>
                                            {capResult.raw_response || capResult.note}
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', mt: 1 }}>
                                        Tokens: {capResult.usage?.input_tokens || 0} in / {capResult.usage?.output_tokens || 0} out
                                        {' · Cost $'}{(capResult.usage?.cost || 0).toFixed(4)}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}

                {skill === 'impairment' && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily: FA_FONT, fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                                ASC 360 impairment-trigger analysis
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField fullWidth size="small" select label="Asset *" value={impForm.asset_id}
                                        onChange={e => setImpForm({ ...impForm, asset_id: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                        <MenuItem value="" sx={{ fontFamily: FA_FONT }}>Select asset</MenuItem>
                                        {assets.map(a => (
                                            <MenuItem key={a.id} value={a.id} sx={{ fontFamily: FA_FONT }}>
                                                {a.asset_number} — {a.name} (BV {formatCurrency(a.book_value)})
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth size="small" multiline minRows={3}
                                        label="Trigger event description *"
                                        placeholder="e.g. equipment damaged in flood; market price dropped 35%; segment loss"
                                        value={impForm.trigger_event_description}
                                        onChange={e => setImpForm({ ...impForm, trigger_event_description: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField fullWidth size="small" type="number" label="Undiscounted Future Cash Flows"
                                        value={impForm.undiscounted_cash_flows}
                                        onChange={e => setImpForm({ ...impForm, undiscounted_cash_flows: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField fullWidth size="small" type="number" label="Fair Value (if measurable)"
                                        value={impForm.fair_value}
                                        onChange={e => setImpForm({ ...impForm, fair_value: e.target.value })}
                                        InputProps={{ sx: { fontFamily: FA_FONT } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                </Grid>
                            </Grid>
                            <Box sx={{ mt: 2 }}>
                                <Button variant="contained" startIcon={<AutoAwesomeIcon />}
                                    onClick={handleImpairment} disabled={impLoading}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                                        '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                                    {impLoading ? 'Analyzing...' : 'Analyze with AI'}
                                </Button>
                                <Button onClick={() => setImpForm({ ...impForm, persist: !impForm.persist })}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', ml: 1, color: impForm.persist ? FA_ACCENT : '#64748b' }}>
                                    {impForm.persist ? '✓ Save as test record' : 'Save as test record'}
                                </Button>
                            </Box>

                            {impResult && (
                                <Box sx={{ mt: 3, p: 2.5, bgcolor: '#f5f3ff', borderRadius: '12px',
                                    border: `1px solid ${FA_ACCENT}33` }}>
                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={impResult.analysis?.requires_test ? 'Test Required' : 'No Test Required'}
                                            sx={{ fontFamily: FA_FONT, fontWeight: 700,
                                                bgcolor: impResult.analysis?.requires_test ? '#fed7aa' : '#dcfce7',
                                                color: impResult.analysis?.requires_test ? '#9a3412' : '#166534' }} />
                                        {impResult.analysis?.trigger_type && (
                                            <Chip label={humanizeStatus(impResult.analysis.trigger_type)}
                                                sx={{ fontFamily: FA_FONT, fontWeight: 600 }} />
                                        )}
                                        {impResult.analysis?.estimated_impairment_loss > 0 && (
                                            <Chip
                                                label={`Est. loss ${formatCurrency(impResult.analysis.estimated_impairment_loss)}`}
                                                sx={{ fontFamily: FA_FONT, fontWeight: 700,
                                                    bgcolor: '#fee2e2', color: '#991b1b' }} />
                                        )}
                                    </Box>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.88rem', color: '#475569' }}>
                                        {impResult.analysis?.reasoning}
                                    </Typography>
                                    {impResult.persisted_test_id && (
                                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.78rem', color: '#16a34a', mt: 1 }}>
                                            Saved as impairment test #{impResult.persisted_test_id}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}

                {skill === 'chat' && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <TextField fullWidth size="small" select label="Asset (optional context)"
                                value={chatAsset} onChange={e => setChatAsset(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                <MenuItem value="" sx={{ fontFamily: FA_FONT }}>Whole asset register</MenuItem>
                                {assets.map(a => (
                                    <MenuItem key={a.id} value={a.id} sx={{ fontFamily: FA_FONT }}>{a.asset_number} — {a.name}</MenuItem>
                                ))}
                            </TextField>

                            <Box sx={{ minHeight: 240, maxHeight: 420, overflowY: 'auto', p: 2,
                                bgcolor: '#f8fafc', borderRadius: '12px', mb: 1.5 }}>
                                {chatHistory.length === 0 ? (
                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8', textAlign: 'center', py: 4 }}>
                                        Ask me about depreciation, impairment, capitalization rules, or specific assets in your register.
                                    </Typography>
                                ) : chatHistory.map((m, i) => (
                                    <Box key={i} sx={{ mb: 1.5, display: 'flex',
                                        justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                        <Box sx={{
                                            maxWidth: '78%',
                                            p: 1.25, borderRadius: '12px',
                                            bgcolor: m.role === 'user' ? FA_ACCENT : '#fff',
                                            color: m.role === 'user' ? '#fff' : '#0f172a',
                                            border: m.role === 'user' ? 'none' : '1px solid #e2e8f0',
                                        }}>
                                            <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>
                                                {m.text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField fullWidth size="small" placeholder="Ask a question..."
                                    value={chatInput}
                                    onChange={e => setChatInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } }}
                                    InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }} />
                                <Button variant="contained" startIcon={<SendIcon />}
                                    onClick={handleChat} disabled={chatLoading || !chatInput.trim()}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                                        '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                                    {chatLoading ? '...' : 'Send'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Box>
        </Box>
    );
}
